'use client';

/**
 * ArchitectureTab — the new default surface under /pipeline?tab=architecture.
 *
 * Replaces the standalone /architecture page. Structured top-to-bottom around
 * an INPUTS → PROCESSES → OUTPUTS narrative so the data flow reads in one
 * pass (Steve directive 2026-05-21: "the architecture must make sense, not
 * be all over the fucken place as it was").
 *
 * Section order (visible top-to-bottom):
 *   1. Critical banner (only if state.criticalCount > 0)
 *   2. Flow row — hourglass + Daily Health grid
 *   3. INPUTS — every pull grouped by source, 5-day strip per row
 *   4. PROCESSES — resolver + scoring + audit + budget
 *   5. OUTPUTS — downstream artifacts table + factor coverage trend
 *   6. RECENT EVENTS — last 5 days, newest-first
 */

import { useResource } from '@/lib/use-resource';
import type { ArchitectureState } from '@/lib/architecture-state';
import { SourcePool } from './architecture/SourcePool';
import { ResolverPinch } from './architecture/ResolverPinch';
import { FactorPillars } from './architecture/FactorPillars';
import { BudgetMeter } from './architecture/BudgetMeter';
import { DailyHealthGrid } from './architecture/DailyHealthGrid';
import { SourcesTimeline } from './architecture/SourcesTimeline';
import { FactorCoverage } from './architecture/FactorCoverage';
import { RecentEvents } from './architecture/RecentEvents';
import { ProcessesSection } from './architecture/ProcessesSection';
import { OutputsSection } from './architecture/OutputsSection';
import { CriticalBanner } from './architecture/CriticalBanner';

function SectionHeading({ id, label, count }: { id: string; label: string; count?: string }): React.JSX.Element {
  return (
    <div id={id} className="flex items-baseline justify-between scroll-mt-20">
      <div className="text-[12px] uppercase tracking-wider text-[#c9a84c]">{label}</div>
      {count && <div className="text-[10px] text-white/40">{count}</div>}
    </div>
  );
}

export function ArchitectureTab(): React.JSX.Element {
  // 3s TTL so the surface polls live during a cron run — same as the
  // retired standalone /architecture page used to.
  const { data } = useResource<ArchitectureState>('/api/architecture-state', 3_000);

  if (!data) {
    return <div className="text-white/40 text-[12px] py-8">Loading architecture state…</div>;
  }

  const inputCount = data.pullTimeline.length;
  const sapiCount = data.pullTimeline.filter((r) => r.source === 'sapi').length;
  const fmpCount = data.pullTimeline.filter((r) => r.source === 'fmp').length;
  const computedCount = data.pullTimeline.filter((r) => r.source === 'computed').length;

  return (
    <div className="space-y-5 pb-8">
      {/* 0. Sticky critical banner */}
      <CriticalBanner state={data} />

      {/* 1. Flow + Daily Health (one row on wide screens, stacked on narrow) */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-md border border-zinc-800 bg-[#0a0e14] p-3">
          <div className="flex items-baseline justify-between">
            <div className="text-[12px] uppercase tracking-wider text-[#c9a84c]">Flow</div>
            <div className="text-[10px] text-white/40">sources → resolver → factors → budget</div>
          </div>
          <div className="mt-2 grid grid-cols-[1fr_72px_1fr] gap-2 items-stretch">
            <div className="space-y-1">
              {data.sources.map((s) => (
                <SourcePool key={s.id} source={s} />
              ))}
            </div>
            <ResolverPinch resolver={data.resolver} />
            <div className="space-y-1">
              <FactorPillars
                pillars={data.pillars}
                stocksScored={data.scoring.stocksScored}
                factorCount={data.scoring.factorCount}
              />
              <BudgetMeter budget={data.budget} />
            </div>
          </div>
        </div>

        <DailyHealthGrid windowDates={data.windowDates} rows={data.dailyHealth} />
      </div>

      {/* 2. INPUTS */}
      <div className="space-y-2">
        <SectionHeading
          id="inputs"
          label="Inputs · every pull grouped by source"
          count={`${inputCount} pulls · SAPI ${sapiCount} · FMP ${fmpCount} · computed ${computedCount}`}
        />
        <SourcesTimeline windowDates={data.windowDates} rows={data.pullTimeline} />
      </div>

      {/* 3. PROCESSES */}
      <div className="space-y-2">
        <SectionHeading id="processes" label="Processes · how inputs become outputs" />
        <ProcessesSection state={data} />
      </div>

      {/* 4. OUTPUTS */}
      <div className="space-y-2">
        <SectionHeading
          id="outputs"
          label="Outputs · what's downstream of scoring"
          count={`${data.outputs.length} artifacts`}
        />
        <OutputsSection outputs={data.outputs} />
        <FactorCoverage windowDates={data.windowDates} rows={data.factorTrend} />
      </div>

      {/* 5. RECENT EVENTS */}
      <div className="space-y-2">
        <SectionHeading id="events" label="Recent events · last 5 days" />
        <RecentEvents events={data.recentEvents} />
      </div>
    </div>
  );
}
